/*
  Softech Store - quantity = number of users, the band follows the quantity (A176, Najam 1 Oct 2026).

  A product has a "band" option when every value of one option is a number range
  ("1-9 users", "10-24 clients", "20000+ users", "1", "2-5") and the ranges do not overlap.
  Model numbers such as "CS110-24" or "LGL100-15" are not bands and are left alone.

  Per-user bands (the band price is a price per user):
    - the quantity is the number of users;
    - typing or stepping the quantity selects the band whose range holds it;
    - picking a band sets the quantity to that band's lowest number when the
      current quantity is outside it;
    - the quantity cannot leave the product's overall range;
    - add to cart always sends the band variant that holds the quantity.
  Whole-band prices (the price rises from one band to the next, e.g. a support
  contract for 1-499 users): no automatic switching; the label says the price
  covers the whole band.

  Pure functions are exported for node tests (module.exports); the DOM part runs
  only in a browser.
*/
(function (root) {
  'use strict';

  var DASH = '[\\-\\u2010\\u2011\\u2012\\u2013\\u2014\\u2212]';
  // number [dash number | +] [space unit words]
  var VALUE_RE = new RegExp('^\\s*(\\d[\\d,]*)\\s*(?:' + DASH + '\\s*(\\d[\\d,]*)|(\\+))?(?:\\s+(.*?))?\\s*$');

  function toInt(s) { return parseInt(String(s).replace(/,/g, ''), 10); }

  function parseBandValue(label) {
    var m = String(label).match(VALUE_RE);
    if (!m) return null;
    var min = toInt(m[1]);
    var max = m[2] ? toInt(m[2]) : (m[3] ? Infinity : min);
    if (max < min) return null;
    return { min: min, max: max, unit: (m[4] || '').trim(), isRange: !!(m[2] || m[3]) };
  }

  // Index of the band option, or -1.
  function findBandOption(optionNames, variants) {
    for (var i = 0; i < optionNames.length; i++) {
      var seen = {}, vals = [];
      variants.forEach(function (v) {
        var val = v.options[i];
        if (val != null && !seen[val]) { seen[val] = 1; vals.push(val); }
      });
      if (vals.length < 2) continue;
      var parsed = vals.map(parseBandValue);
      if (parsed.some(function (p) { return !p; })) continue;
      if (!parsed.some(function (p) { return p.isRange; })) continue;
      parsed.sort(function (a, b) { return a.min - b.min; });
      var ok = true;
      for (var k = 1; k < parsed.length; k++) if (parsed[k].min <= parsed[k - 1].max) { ok = false; break; }
      if (ok) return i;
    }
    return -1;
  }

  function singular(word) {
    return String(word).split(/\s+/).map(function (w) {
      if (/^and$/i.test(w)) return 'or';
      if (/^GBs$/i.test(w)) return 'GB';
      if (w.length > 3 && /[^s]s$/i.test(w)) return w.slice(0, -1);
      return w;
    }).join(' ');
  }

  // Analyse product data -> model or null (no band option).
  function analyse(data) {
    var names = data.options || [];
    var variants = data.variants || [];
    var bi = findBandOption(names, variants);
    if (bi < 0) return null;
    // Storage volume bands (Avast Cloud Backup "100-400 GBs") are not priced per unit: left as standard.
    if (/volume/i.test(names[bi])) return null;

    // Unit word: from the band labels ("1-9 clients" -> "clients"), else from the option or product name.
    var unit = '';
    variants.some(function (v) { var p = parseBandValue(v.options[bi]); if (p && p.unit) { unit = p.unit; return true; } return false; });
    if (!unit) {
      if (/\bGB\b/i.test(data.title || '')) unit = 'GB';
      else if (/per[\s-]+instanc/i.test(data.title || '')) unit = 'instances';
      else if (/per[\s-]+databas/i.test(data.title || '')) unit = 'databases';
      else if (/asset/i.test(data.title || '')) unit = 'assets';
      else if (/technician/i.test(data.title || '')) unit = 'technicians';
      else if (/databas/i.test(data.title || '')) unit = 'databases';
      else if (/instanc/i.test(data.title || '')) unit = 'instances';
      else if (/seat/i.test(data.title || '')) unit = 'seats';
      else if (/network device|infrastructure host/i.test(data.title || '')) unit = 'devices';
      else unit = 'users';
    }
    var unitOne = singular(unit);

    // Whole-band pricing: the second band costs more than 20% above the first one
    // within the same combination of the other options.
    var combos = {};
    variants.forEach(function (v) {
      var key = v.options.filter(function (_, i) { return i !== bi; }).join('||');
      (combos[key] = combos[key] || []).push(v);
    });
    var flat = false;
    Object.keys(combos).forEach(function (k) {
      var list = combos[k].map(function (v) { return { r: parseBandValue(v.options[bi]), p: v.price }; })
        .sort(function (a, b) { return a.r.min - b.r.min; });
      // Whole-band products rise at once (1-499 users: 663, 500-999 users: 947).
      if (list.length > 1 && list[1].p > list[0].p * 1.2) flat = true;
    });

    return { bandIndex: bi, optionNames: names, variants: variants, unit: unit, unitOne: unitOne, flat: flat,
             min10: !!data.min10 && !flat, // Task 7399: minimum 10 (the 1-9 band is hidden)
             // BR-056 amend 2: the minimum itself (10 users, 100 assets, 10 GB) from snippets/softech-min10-rule.
             minQty: (!!data.min10 && !flat) ? (parseInt(data.min_qty, 10) || 10) : 1,
             moneyFormat: data.money_format || '${{amount}}' };
  }

  // Bands available for the current values of the other options, sorted by min.
  function bandsFor(model, selected) {
    var bi = model.bandIndex, out = [];
    model.variants.forEach(function (v) {
      for (var i = 0; i < v.options.length; i++) if (i !== bi && v.options[i] !== selected[i]) return;
      var r = parseBandValue(v.options[bi]);
      out.push({ value: v.options[bi], min: r.min, max: r.max, variant: v });
    });
    out.sort(function (a, b) { return a.min - b.min; });
    return out;
  }

  // Overall range of the product: lowest band start to highest band end, inside the
  // store's own quantity rule of the first band (minimum) and the highest maximum set.
  function limits(bands, floor) {
    if (!bands.length) return { min: Math.max(1, floor || 1), max: Infinity };
    var min = Math.max(1, floor || 1, bands[0].min, bands[0].variant.min || 1);
    var max = bands[bands.length - 1].max;
    var vmaxes = bands.map(function (b) { return b.variant.max; }).filter(function (x) { return x != null; });
    if (vmaxes.length === bands.length) max = Math.min(max, Math.max.apply(null, vmaxes));
    return { min: min, max: max };
  }

  function clamp(qty, lim) {
    if (!isFinite(qty) || isNaN(qty)) return lim.min;
    qty = Math.floor(qty);
    if (qty < lim.min) return lim.min;
    if (qty > lim.max) return lim.max;
    return qty;
  }

  // Band that holds qty; in a gap between bands, the next band up.
  function bandForQty(bands, qty) {
    for (var i = 0; i < bands.length; i++) if (qty >= bands[i].min && qty <= bands[i].max) return bands[i];
    for (var j = 0; j < bands.length; j++) if (bands[j].min > qty) return bands[j];
    return bands[bands.length - 1] || null;
  }

  function rangeText(b) {
    if (b.max === Infinity) return b.min + '+';
    if (b.max === b.min) return String(b.min);
    return b.min + '-' + b.max;
  }

  function formatMoney(cents, format) {
    cents = Math.round(Number(cents) || 0);
    function fmt(n, dec, th, ds) {
      var neg = n < 0; n = Math.abs(n);
      var s = (n / 100).toFixed(dec);
      var parts = s.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, th);
      return (neg ? '-' : '') + parts[0] + (parts[1] ? ds + parts[1] : '');
    }
    return String(format || '${{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, function (_, key) {
      switch (key) {
        case 'amount_no_decimals': return fmt(cents, 0, ',', '.');
        case 'amount_with_comma_separator': return fmt(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return fmt(cents, 2, "'", '.');
        default: return fmt(cents, 2, ',', '.');
      }
    });
  }

  function plural(model, n) { return n === 1 ? model.unitOne : model.unit; }

  // Texts shown under the quantity field.
  function texts(model, band, qty) {
    var price = formatMoney(band.variant.price, model.moneyFormat);
    var total = formatMoney(band.variant.price * qty, model.moneyFormat);
    var bandTxt = rangeText(band) + ' ' + model.unit;
    if (model.flat) {
      return {
        label: 'Quantity (number of licences)',
        line: 'Band: ' + bandTxt + ' · ' + price + ' covers the whole band · Total for ' + qty + (qty === 1 ? ' licence' : ' licences') + ': ' + total,
        note: 'This price covers the whole band (' + bandTxt + '). Choose the band that fits your number of ' + model.unit + ' and keep quantity 1.'
      };
    }
    return {
      label: 'Number of ' + model.unit,
      line: 'Band: ' + bandTxt + ' · ' + price + ' per ' + model.unitOne + ' · Total for ' + qty + ' ' + plural(model, qty) + ': ' + total,
      note: 'Price is per ' + model.unitOne + '. Enter the number of ' + model.unit + ' you need - e.g. ' +
        (model.min10 ? (model.minQty * 2.5) + ' ' + model.unit + ' = quantity ' + (model.minQty * 2.5) + '.' : '5 ' + model.unit + ' = quantity 5.')
    };
  }

  var api = { parseBandValue: parseBandValue, findBandOption: findBandOption, analyse: analyse, bandsFor: bandsFor,
              limits: limits, clamp: clamp, bandForQty: bandForQty, rangeText: rangeText, formatMoney: formatMoney, texts: texts };

  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  root.SoftechBands = api;

  if (typeof document === 'undefined') return;

  // ------------------------------------------------------------------ DOM part
  function Controller(sectionId, model) {
    this.sid = sectionId;
    this.m = model;
    this.qty = null;
    this.programmatic = false;
    this.timer = null;
    this.noticeTimer = null;
  }

  Controller.prototype.input = function () { return document.getElementById('Quantity-' + this.sid); };
  Controller.prototype.selects = function () { return document.getElementById('variant-selects-' + this.sid); };
  Controller.prototype.form = function () { return document.getElementById('product-form-' + this.sid); };

  // One entry per option, in option order: {type:'select', el} or {type:'radio', items}
  Controller.prototype.controls = function () {
    var vs = this.selects();
    if (!vs) return [];
    return Array.prototype.map.call(vs.querySelectorAll('.product-form__input'), function (wrap) {
      var sel = wrap.querySelector('select');
      if (sel) return { type: 'select', el: sel };
      return { type: 'radio', items: wrap.querySelectorAll('input[type="radio"]') };
    });
  };

  Controller.prototype.selected = function () {
    return this.controls().map(function (c) {
      if (c.type === 'select') return c.el.value;
      for (var i = 0; i < c.items.length; i++) if (c.items[i].checked) return c.items[i].value;
      return null;
    });
  };

  Controller.prototype.bands = function () { return bandsFor(this.m, this.selected()); };

  Controller.prototype.currentBand = function (bands) {
    var cur = this.selected()[this.m.bandIndex];
    for (var i = 0; i < bands.length; i++) if (bands[i].value === cur) return bands[i];
    return null;
  };

  Controller.prototype.setInput = function (q) {
    var el = this.input();
    if (!el) return;
    if (String(el.value) !== String(q)) el.value = q;
    var lim = limits(this.bands(), this.m.minQty);
    if (!this.m.flat) {
      el.min = lim.min;
      el.setAttribute('data-min', lim.min);
      if (lim.max !== Infinity) { el.max = lim.max; el.setAttribute('data-max', lim.max); }
    }
    var qi = el.closest('quantity-input');
    if (qi && qi.validateQtyRules) qi.validateQtyRules();
  };

  Controller.prototype.switchBand = function (band) {
    var c = this.controls()[this.m.bandIndex];
    if (!c) return;
    var target = null;
    if (c.type === 'select') {
      c.el.value = band.value;
      target = c.el;
    } else {
      for (var i = 0; i < c.items.length; i++) if (c.items[i].value === band.value) { c.items[i].checked = true; target = c.items[i]; }
    }
    if (!target) return;
    // The form posts the right variant at once, even before the page finishes redrawing.
    var idIn = this.form() && this.form().querySelector('input[name="id"]');
    if (idIn) idIn.value = band.variant.id;
    this.programmatic = true;
    try { target.dispatchEvent(new Event('change', { bubbles: true })); } finally { this.programmatic = false; }
    this.notice('Switched to the ' + rangeText(band) + ' ' + this.m.unit + ' band');
  };

  // Apply the quantity: keep it in range and select its band.
  Controller.prototype.commit = function () {
    clearTimeout(this.timer); this.timer = null;
    var bands = this.bands();
    if (!bands.length) return;
    var lim = limits(bands, this.m.minQty);
    var raw = parseInt(this.input() ? this.input().value : this.qty, 10);
    if (isNaN(raw)) raw = this.qty || lim.min;
    var q = this.m.flat ? Math.max(1, raw) : clamp(raw, lim);
    if (!this.m.flat && q !== raw) {
      this.notice(q > raw ? 'The minimum for this product is ' + lim.min + ' ' + plural(this.m, lim.min)
                          : 'The maximum for this product is ' + lim.max + ' ' + plural(this.m, lim.max));
    }
    this.qty = q;
    this.setInput(q);
    if (!this.m.flat) {
      var want = bandForQty(bands, q);
      if (want && q < want.min) { q = want.min; this.qty = q; this.setInput(q); }
      var cur = this.currentBand(bands);
      if (want && (!cur || cur.value !== want.value)) this.switchBand(want);
    }
    this.render();
  };

  // The user picked a band in the dropdown.
  Controller.prototype.onBandPicked = function () {
    if (this.m.flat) { this.render(); return; }
    var bands = this.bands(), b = this.currentBand(bands);
    if (!b) return;
    if (this.qty == null || this.qty < b.min || this.qty > b.max) {
      this.qty = b.min;
      this.setInput(b.min);
      this.notice('Quantity set to ' + b.min + ' ' + plural(this.m, b.min) + ' for the ' + rangeText(b) + ' ' + this.m.unit + ' band');
    }
    this.render();
  };

  // After the theme redraws the product (it resets the quantity to 1), put the quantity back.
  Controller.prototype.afterRedraw = function () {
    if (this.qty == null) return;
    this.setInput(this.qty);
    if (!this.m.flat) {
      var bands = this.bands(), cur = this.currentBand(bands), want = bandForQty(bands, this.qty);
      if (want && cur && cur.value !== want.value && !this.timer) { this.commit(); return; }
    }
    this.render();
  };

  Controller.prototype.render = function () {
    var bands = this.bands();
    var line = document.getElementById('SoftechBandLine-' + this.sid);
    if (!line || !bands.length) return;
    var q = parseInt(this.input() ? this.input().value : this.qty, 10);
    if (isNaN(q) || q < 1) q = this.qty || bands[0].min;
    var band = this.m.flat ? (this.currentBand(bands) || bands[0]) : bandForQty(bands, q);
    var t = texts(this.m, band, q);
    line.textContent = t.line;
    document.getElementById('SoftechBandNote-' + this.sid).textContent = t.note;
    document.getElementById('SoftechBandLabel-' + this.sid).textContent = t.label;
  };

  Controller.prototype.notice = function (msg) {
    var n = document.getElementById('SoftechBandNotice-' + this.sid);
    if (!n) return;
    n.textContent = msg;
    n.hidden = false;
    n.classList.remove('sb-flash'); void n.offsetWidth; n.classList.add('sb-flash');
    clearTimeout(this.noticeTimer);
    this.noticeTimer = setTimeout(function () { n.hidden = true; }, 6000);
  };

  Controller.prototype.buildUi = function () {
    var form = document.getElementById('Quantity-Form-' + this.sid);
    if (!form || document.getElementById('SoftechBand-' + this.sid)) return false;
    var wrap = form.parentNode;
    wrap.classList.add('sb-on');
    var label = document.createElement('label');
    label.className = 'sb-label';
    label.id = 'SoftechBandLabel-' + this.sid;
    label.htmlFor = 'Quantity-' + this.sid;
    wrap.insertBefore(label, form);
    var box = document.createElement('div');
    box.className = 'sb-box';
    box.id = 'SoftechBand-' + this.sid;
    box.innerHTML =
      '<p class="sb-line" id="SoftechBandLine-' + this.sid + '" aria-live="polite"></p>' +
      '<p class="sb-notice" id="SoftechBandNotice-' + this.sid + '" role="status" hidden></p>' +
      '<p class="sb-note" id="SoftechBandNote-' + this.sid + '"></p>' +
      (this.m.min10 ? '<p class="sb-min" id="SoftechBandMin-' + this.sid + '">Minimum ' + this.m.minQty + ' ' + this.m.unit +
        '. For fewer, <a href="/pages/contact#quote-form" class="link" data-sb-quote>request a quote</a>.</p>' : '');
    form.parentNode.insertBefore(box, form.nextSibling);
    return true;
  };

  Controller.prototype.isBandTarget = function (t) {
    var c = this.controls()[this.m.bandIndex];
    if (!c || !t) return false;
    if (c.type === 'select') return t === c.el;
    return Array.prototype.indexOf.call(c.items, t) !== -1;
  };

  Controller.prototype.start = function () {
    var self = this;
    if (!this.buildUi()) return;
    // "request a quote" opens the product page's own quote form (softech-product-cta) when it is there.
    document.addEventListener('click', function (e) {
      var a = e.target.closest ? e.target.closest('[data-sb-quote]') : null;
      if (!a) return;
      var b = document.querySelector('[data-sbq-quote]');
      if (b) { e.preventDefault(); b.click(); }
    });
    var bands = this.bands();
    var cur = this.currentBand(bands);
    var q = parseInt(this.input() ? this.input().value : 1, 10) || 1;
    if (!this.m.flat && cur && (q < cur.min || q > cur.max)) q = cur.min; // e.g. a band starting at 25
    this.qty = q;
    this.setInput(q);
    this.render();

    document.addEventListener('input', function (e) {
      if (e.target !== self.input()) return;
      var v = parseInt(e.target.value, 10);
      if (!isNaN(v)) self.qty = v;
      self.render();
      clearTimeout(self.timer);
      self.timer = setTimeout(function () { self.commit(); }, 700);
    });
    document.addEventListener('change', function (e) {
      if (e.target === self.input()) { self.commit(); return; }
      if (self.programmatic) return;
      if (self.isBandTarget(e.target)) { self.onBandPicked(); return; }
      if (e.target.name === 'id' && e.target.closest && e.target.closest('#product-form-' + self.sid)) {
        setTimeout(function () { self.afterRedraw(); }, 0);
      }
    });
    document.addEventListener('keydown', function (e) {
      if (e.target === self.input() && e.key === 'Enter') self.commit();
    });
    // Add to cart: make sure the band variant and the quantity are the final ones.
    document.addEventListener('submit', function (e) {
      if (e.target === self.form()) self.commit();
    }, true);
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.quantityUpdate, function () { setTimeout(function () { self.afterRedraw(); }, 0); });
    }
  };

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('script[id^="SoftechBandData-"]'), function (s) {
      if (s.dataset.sbStarted) return;
      s.dataset.sbStarted = '1';
      var data;
      try { data = JSON.parse(s.textContent); } catch (e) { return; }
      var model = analyse(data);
      if (!model) return; // no band option: the quantity field behaves as standard
      new Controller(s.id.replace('SoftechBandData-', ''), model).start();
    });
  }
  api.Controller = Controller;
  api.boot = boot;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})(typeof window !== 'undefined' ? window : this);
